import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';

import Fullscreen from '../../../global/Fullscreen';
import { fetchVideoToken } from '../../../../actions/sessions';
import PhoneCallParticipants from '../../practitioner_video/Vonage/PhoneCallParticipants';
import localeMessage from '../localeMessage';
import FullscreenIcon from './full.png';
import { useVonageSession } from '../../vonage/useVonageSession';
import { useVoiceCalls } from '../../vonage/useVoiceCalls';
import { useVonageDeviceSettings } from '../../vonage/device_settings/useVonageDeviceSettings';
import LiveCaptionsPanel from '../../vonage/live_captions/LiveCaptionsPanel';
import { resolveCaptionSpeaker } from '../../vonage/live_captions/resolveCaptionSpeaker';
import { useLiveCaptions } from '../../vonage/live_captions/useLiveCaptions';

export const VonageScreen = ({
  appointment,
  sessionType,
  videoToken,
  user,
  locale,
  couplesSameLocation = false,
  initialSettings,
  onDeviceSettingsReady = () => {},
  onLiveCaptionsReady = () => {},
  ...props
}) => {
  const [showYouAreNowConnectedMessage, setShowYouAreNowConnectedMessage] =
    useState(false);
  const [cameraOn, setCameraOn] = useState(
    Boolean(initialSettings.cameraDeviceId && initialSettings.isCameraEnabled),
  );
  const [micOn, setMicOn] = useState(initialSettings.isMicrophoneEnabled);
  const [fullScreen, setFullScreen] = useState(false);
  const { voiceCalls, onPhoneCallConnected, onPhoneCallDisconnected } =
    useVoiceCalls();
  const resolveSpeaker = useCallback(
    event =>
      resolveCaptionSpeaker({
        appointment,
        event,
        voiceCalls,
        phoneSpeakerName: last4 =>
          localeMessage('liveCaptions.phoneSpeaker', locale, { last4 }),
      }),
    [appointment, locale, voiceCalls],
  );
  const liveCaptions = useLiveCaptions({ appointment, resolveSpeaker });
  const onLiveCaptionsChange = useCallback(
    enabled => {
      if (enabled) liveCaptions.enableCaptions();
      else liveCaptions.disableCaptions();
    },
    [liveCaptions.enableCaptions, liveCaptions.disableCaptions],
  );
  const onJoined = useCallback(() => {
    if (initialSettings.isLiveCaptionsEnabled) {
      liveCaptions.enableCaptions();
    }
  }, [initialSettings.isLiveCaptionsEnabled, liveCaptions.enableCaptions]);

  useEffect(() => {
    props.fetchVideoToken(appointment.id);
  }, [appointment.id, props.fetchVideoToken]);

  const getDivId = userId => {
    if (!appointment?.id) return 'remote-media-div';

    if (appointment.provider_type === 'couples') {
      if (
        String(userId) ===
        String(appointment.couples_appointment_detail?.provider?.id)
      )
        return 'remote-provider-media';

      return 'remote-partner-media';
    }
    return 'remote-media-div';
  };

  const {
    connected,
    participantPresent: providerPresent,
    partnerPresent,
    setCameraEnabled,
    setMicEnabled,
    publisher,
    hasVideoSource,
    error,
  } = useVonageSession({
    appointment,
    user,
    token: videoToken?.token,
    role: 'client',
    getDivId,
    couplesPartnerDivId: 'remote-partner-media',
    phoneCallMediaDivId: 'phone-call-media',
    onPhoneCallConnected,
    onPhoneCallDisconnected,
    initialSettings,
    onJoined,
    captionsEnabled: liveCaptions.isEnabled,
    onCaptionReceived: liveCaptions.onCaptionReceived,
    onCaptionStreamDestroyed: liveCaptions.onCaptionStreamDestroyed,
    onCaptionsInterrupted: liveCaptions.clearPartialCaptions,
  });

  const deviceSettings = useVonageDeviceSettings({
    publisher,
    hasVideoSource,
    isMicrophoneEnabled: micOn,
    isCameraEnabled: cameraOn,
    initialSettings,
  });

  useEffect(() => {
    onDeviceSettingsReady({ controller: deviceSettings, hasVideoSource });
    // The controller object is rebuilt by the hook; publish only meaningful
    // state changes so a parent render cannot create an update loop.
  }, [
    hasVideoSource,
    deviceSettings.selectedMicrophoneId,
    deviceSettings.selectedCameraId,
    deviceSettings.selectedSpeakerId,
    deviceSettings.changing,
    deviceSettings.errors,
    deviceSettings.micLevel,
    deviceSettings.playing,
    onDeviceSettingsReady,
  ]);

  useEffect(() => {
    onLiveCaptionsReady({
      available: liveCaptions.available,
      isEnabled: liveCaptions.isEnabled,
      isStarting: liveCaptions.isStarting,
      onChange: onLiveCaptionsChange,
    });
  }, [
    liveCaptions.available,
    liveCaptions.isEnabled,
    liveCaptions.isStarting,
    onLiveCaptionsChange,
    onLiveCaptionsReady,
  ]);

  useEffect(() => {
    if (props.callbackForControls) {
      props.callbackForControls({
        toggleCamera: () => {
          if (!hasVideoSource) return false;
          const newState = !cameraOn;
          setCameraOn(newState);
          setCameraEnabled(newState);
          return true;
        },
        toggleMic: () => {
          const newState = !micOn;
          setMicOn(newState);
          setMicEnabled(newState);
        },
      });
    }
  }, [
    props.callbackForControls,
    cameraOn,
    micOn,
    setCameraEnabled,
    setMicEnabled,
    hasVideoSource,
  ]);

  useEffect(() => {
    if (connected) {
      setShowYouAreNowConnectedMessage(true);
      setTimeout(() => {
        setShowYouAreNowConnectedMessage(false);
      }, 3000);
    }
  }, [connected]);

  const renderUnableMessage = () => (
    <p className="unable-connect-message">
      {localeMessage('unableTo', locale)}
      <b
        role="button"
        tabIndex={0}
        onClick={() => window.location.reload()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            window.location.reload();
          }
        }}
        style={{ textDecoration: 'underline' }}
      >
        {localeMessage('here', locale)}
      </b>
    </p>
  );

  const renderCouplesRemotes = () => {
    if (sessionType !== 'couples' || couplesSameLocation) {
      return <div />;
    }

    return (
      <div className="remote-couples">
        <div id="remote-provider-media" className="remote-couples-media">
          {!providerPresent && <h1>{localeMessage('yourCare', locale)}</h1>}
        </div>
        <div id="remote-partner-media" className="remote-couples-media">
          {!partnerPresent && <h1>{localeMessage('yourCare', locale)}</h1>}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`not-connected-screen${
        liveCaptions.isEnabled ? ' has-live-captions' : ''
      }`}
    >
      <Fullscreen
        enabled={fullScreen}
        onChange={isFull => setFullScreen(isFull)}
      >
        <div id="remote-media-div" className="full-screenable-node">
          {voiceCalls.length > 0 && (
            <PhoneCallParticipants voiceCalls={voiceCalls} />
          )}
          <div
            style={{
              display:
                sessionType === 'couples' && !couplesSameLocation
                  ? 'none'
                  : 'block',
            }}
          >
            {!providerPresent && <h1>{localeMessage('yourCare', locale)}</h1>}
          </div>
          {renderCouplesRemotes()}
          <div id="local-media" style={{ borderRadius: 8 }} />
          <div id="phone-call-media" style={{ display: 'none' }} />
          <img
            role="presentation"
            src={FullscreenIcon}
            alt="full-screen"
            className="full-screen-icon"
            onClick={() => setFullScreen(!fullScreen)}
          />
          {showYouAreNowConnectedMessage && (
            <div className="you-are-now-connected-message-box">
              <p>{localeMessage('youAreNowConnected', locale)}</p>
            </div>
          )}
          {error && renderUnableMessage()}
        </div>
        {liveCaptions.isEnabled && (
          <div className="client-live-captions">
            <LiveCaptionsPanel
              captionState={liveCaptions.captionState}
              languageCode={liveCaptions.languageCode}
              onClose={liveCaptions.disableCaptions}
              t={id => localeMessage(id, locale)}
            />
          </div>
        )}
      </Fullscreen>
    </div>
  );
};

VonageScreen.propTypes = {
  userId: PropTypes.number.isRequired,
  channel: PropTypes.string.isRequired,
  sessionType: PropTypes.string.isRequired,

  appointment: PropTypes.object.isRequired,
  couplesSameLocation: PropTypes.bool,
  locale: PropTypes.string.isRequired,
  videoToken: PropTypes.object,
  user: PropTypes.object.isRequired,
  fetchVideoToken: PropTypes.func.isRequired,
  callbackForControls: PropTypes.func.isRequired,
  initialSettings: PropTypes.object.isRequired,
  onDeviceSettingsReady: PropTypes.func,
  onLiveCaptionsReady: PropTypes.func,
};

function mapStateToProps(state) {
  const videoToken = _.get(state, 'session.videoToken');

  return {
    locale: (state.user && state.user.locale) || 'en',
    videoToken,
  };
}

export default connect(mapStateToProps, {
  fetchVideoToken,
})(VonageScreen);
